import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';


const GridWrapper = styled.div`
    display: grid;
    grid-template-columns: 2fr 2fr;
    grid-template-rows: auto;
    gap: 20px;
    padding: 140px 30px;
    max-width: 12000px; 
    margin: 0 auto;
    background-color: white;
    align-items: center;
    max-height: 379px;

    @media (max-width: 768px) {
        display: flex;
        flex-direction: column;
        grid-template-columns: 1fr;
        padding: 100px 20px 40px 20px;
        max-height: none;
        height: auto;
        margin-top: 0;
    }
`;

const Column = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    height: 100%;

    @media (max-width: 768px) {
        gap: 20px;
        width: 100%;
        height: auto;
    }
`;

const MotionCard = styled(motion.div)`
    background-color: #f7f7f9;
    border-radius: 24px;
    padding: 21px;
    text-align: left;
    max-height: 220px;
    font-size: 16px;
    flex: 1;
    overflow: hidden;

    @media (max-width: 768px) {
        padding: 24px;
        font-size: 15px;
        max-height: none;
        height: auto;
        flex: none;
    }
`;

const ExperienceCard = styled(motion.div)`
    background-color: #f7f7f9;
    border-radius: 24px;
    padding: 21px;
    text-align: left;
    font-size: 16px;
    max-height: 500px;
    flex: 1;
    position: relative;
    display: flex;
    flex-direction: column;

    @media (max-width: 768px) {
        padding: 24px;
        font-size: 15px;
        max-height: none; 
        min-height: 300px; 
        height: auto;
        flex: none;
    }
`;

const Title = styled.h3`
    font-size: 22px;
    margin-bottom: 8px;
    margin-top: 3px;

    @media (max-width: 768px) {
        font-size: 20px;
        padding-right: 60px; 
    }
`;

const Text = styled.p`
    color: #666;
    font-size: 17px;
    margin-top: -1px;
    line-height: 1.5;

    @media (max-width: 768px) {
        font-size: 15px;
        line-height: 1.6; 
    }
`;


const ControlsContainer = styled.div`
    position: absolute;
    top: 21px;
    right: 21px;
    display: flex;
    gap: 10px;
    z-index: 10;

    @media (max-width: 768px) {
        top: 24px;
        right: 24px;
    }
`;

const ArrowButton = styled.button`
    background-color: white;
    border: 1px solid #ddd;
    border-radius: 50%;
    width: 35px;
    height: 35px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);

    &:hover {
        background-color: #24292e;
        border-color: #24292e;
        transform: scale(1.1);

        svg {
            fill: white;
        }
    }

    svg {
        width: 18px;
        height: 18px;
        fill: #333;
        transition: fill 0.2s ease;
    }
`;

const fadeUp = {
    initial: { opacity: 0, y: 40 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
};

const AboutPage = () => {
    const experiences = [
        {
            id: 1,
            role: "Full-Stack Developer",
            company: "Agrarco LLC (2025-Current)",
            content: (
                <>
                    At Agrarco, I worked as a Full Stack Developer, building and maintaining a custom enterprise system designed specifically for agricultural and factory operations.<br /> I was responsible for developing a complete solution that connected physical factory devices to digital systems, enabling real-time automation and data-driven decision-making.<br /> <br />One of the core projects I worked on was an integrated purchase management application that allows employees to register and process product purchases directly through the ERP system.<br /> The application connects to large factory weighing scales, automatically capturing weight data as trucks arrive and stand on them.<br /> This automation eliminated manual entry errors, increased transaction speed, and ensured accurate data flow between departments.
                </>
            )
        },
        {
            id: 2,
            role: "Backend Developer Intern",
            company: "IDRAK Technology Transfer (2023-2025)",
            content: (
                <>
                    Worked as an intern with a focus on backend web development.<br />
                    I had the opportunity to work on real-world projects where I helped build and maintain RESTful APIs using Spring Boot and managed data with PostgreSQL.<br />
                    I was involved in developing internal tools, writing clean, reusable code, and contributing to how the backend architecture was structured.<br />
                    <br />
                    Beyond specific feature implementation, I played a key role in developing internal tools to streamline operations. I focused heavily on writing clean, reusable, and self-documenting code, while also contributing to discussions regarding backend architectural patterns to ensure scalability.<br /><br />These experiences not only sharpened my technical skills but also gave me a deeper understanding of how to communicate complex technical ideas and work effectively within a professional engineering team. </>
            )
        }
    ];

    const [currentIndex, setCurrentIndex] = useState(0);
    const [isAutoPlaying, setIsAutoPlaying] = useState(true);

    useEffect(() => {
        if (!isAutoPlaying) return;

        const interval = setInterval(() => {
            setCurrentIndex((prevIndex) => (prevIndex + 1) % experiences.length);
        }, 5000);

        return () => clearInterval(interval);
    }, [currentIndex, isAutoPlaying, experiences.length]);

    const handleManualNext = () => {
        setIsAutoPlaying(false);
        setCurrentIndex((prevIndex) => (prevIndex + 1) % experiences.length);
    };

    const handleManualPrev = () => {
        setIsAutoPlaying(false);
        setCurrentIndex((prevIndex) =>
            prevIndex === 0 ? experiences.length - 1 : prevIndex - 1
        );
    };

    const currentExp = experiences[currentIndex];

    return (
        <GridWrapper>
            <Column>
                <MotionCard {...fadeUp}>
                    <Title>About Me</Title>
                    <Text>I was born and raised in Baku, Azerbaijan. Since childhood, I’ve had a passion for bringing my ideas to life, and programming became the perfect way to do that. It gives me the freedom to turn imagination into something real — whether it’s a tool, a system, or a full application. For me, software development is more than just coding; it’s an unlimited workspace where creativity meets logic, and every project is a new opportunity to build something meaningful.</Text>
                </MotionCard>

                <MotionCard {...fadeUp} transition={{ duration: 0.6, delay: 0.1 }}>
                    <Title>Education</Title>
                    <Text><b style={{ fontWeight: 600 }}>BSc in Information Technology</b><br />
                        <b style={{ fontWeight: 600 }}>Azerbaijani State University of Economics (2024-2028)</b><br />
                        <br />
                        Currently studying at the Faculty of Information Technology, where I’ve developed a strong foundation in computer science. Through my coursework, I’ve explored programming, algorithms, data structures, and software engineering — all of which have helped me grow both technically and logically as a developer.</Text>
                </MotionCard>
            </Column>

            <Column>
                <ExperienceCard {...fadeUp} transition={{ duration: 0.6, delay: 0.1 }}>
                    <Title>Experience</Title>

                    <ControlsContainer>
                        <ArrowButton onClick={handleManualPrev} aria-label="Previous Experience">
                            <svg viewBox="0 0 24 24">
                                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                            </svg>
                        </ArrowButton>

                        <ArrowButton onClick={handleManualNext} aria-label="Next Experience">
                            <svg viewBox="0 0 24 24">
                                <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
                            </svg>
                        </ArrowButton>
                    </ControlsContainer>

                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentIndex}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                        >
                            <Text>
                                <b style={{ fontWeight: 600 }}>{currentExp.role}</b><br />
                                <b style={{ fontWeight: 600 }}>{currentExp.company}</b><br />
                                <br />
                                {currentExp.content}
                            </Text>
                        </motion.div>
                    </AnimatePresence>
                </ExperienceCard>
            </Column>
        </GridWrapper>
    );
};

export default AboutPage;
